import { useEffect, useRef, useState } from 'react'
import { Animated, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import Constants from 'expo-constants'
import { colors } from '@chillout/core'
import type { AdminAccount } from '../auth/adminAccount'
import { ADMIN_SECTION_ROUTES } from '../adminRoutes'
import type { AdminSectionRouteName } from '../adminRoutes'
import { AdminMenuFooter } from './AdminMenuFooter'
import { AdminMenuHeader } from './AdminMenuHeader'
import { AdminMenuItem } from './AdminMenuItem'
import { COMPACT_MENU_WIDTH, FULL_MENU_WIDTH } from './constants'

type AdminMenuVariant = 'full' | 'compact'
type AdminMenuPresentation = 'inline' | 'inlineExpandable' | 'overlay'

interface AdminMenuPanelProps {
  activeRouteName: AdminSectionRouteName
  adminAccount: AdminAccount
  variant?: AdminMenuVariant
  presentation?: AdminMenuPresentation
  isOpen?: boolean
  collapsedWidth?: number
  onClose?: () => void
  onOpen?: () => void
  onRoutePress: (routeName: AdminSectionRouteName) => void
  onSignOut: () => void
}

export function AdminMenuPanel({
  activeRouteName,
  adminAccount,
  variant = 'full',
  presentation = 'inline',
  isOpen = true,
  collapsedWidth,
  onClose,
  onOpen,
  onRoutePress,
  onSignOut,
}: AdminMenuPanelProps) {
  const { t } = useTranslation()
  const appVersion = Constants.expoConfig?.version
  const slideProgress = useRef(new Animated.Value(isOpen ? 1 : 0)).current
  const [shouldRenderOverlay, setShouldRenderOverlay] = useState(isOpen)
  const [shouldRenderFullInlineMenu, setShouldRenderFullInlineMenu] = useState(isOpen)
  const displayedVariant =
    presentation === 'inlineExpandable' ? (shouldRenderFullInlineMenu ? 'full' : 'compact') : variant
  const isFull = displayedVariant === 'full'
  const menuWidth = isFull ? FULL_MENU_WIDTH : COMPACT_MENU_WIDTH
  const expandsFromCompact = presentation === 'overlay' && collapsedWidth !== undefined

  useEffect(() => {
    if (presentation !== 'overlay' && presentation !== 'inlineExpandable') return

    if (isOpen) {
      setShouldRenderOverlay(true)
      setShouldRenderFullInlineMenu(true)
    }

    Animated.timing(slideProgress, {
      toValue: isOpen ? 1 : 0,
      duration: 180,
      useNativeDriver: false,
    }).start(({ finished }) => {
      if (!finished || isOpen) return

      if (presentation === 'overlay') {
        setShouldRenderOverlay(false)
      }

      if (presentation === 'inlineExpandable') {
        setShouldRenderFullInlineMenu(false)
      }
    })
  }, [isOpen, presentation, slideProgress])

  function handleRoutePress(routeName: AdminSectionRouteName) {
    onRoutePress(routeName)
    if (presentation === 'overlay') {
      onClose?.()
    }
  }

  const menu = (
    <View
      className={`${isFull ? 'w-[240px] pl-7 pr-8' : 'w-[76px] px-4'} shrink-0 bg-canvas-dark pb-6 pt-8`}
      style={styles.menu}
    >
      <AdminMenuHeader isFull={isFull} onOpen={onOpen} />

      <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
        {ADMIN_SECTION_ROUTES.map((route) => (
          <AdminMenuItem
            key={route.name}
            route={route}
            isActive={activeRouteName === route.name}
            isFull={isFull}
            onPress={handleRoutePress}
          />
        ))}
      </ScrollView>

      <AdminMenuFooter adminAccount={adminAccount} isFull={isFull} onSignOut={onSignOut} />

      {isFull && appVersion ? (
        <Text style={styles.version}>v{appVersion}</Text>
      ) : null}
    </View>
  )

  if (presentation === 'inline') {
    return menu
  }

  if (presentation === 'inlineExpandable') {
    const inlineWidth = slideProgress.interpolate({
      inputRange: [0, 1],
      outputRange: [COMPACT_MENU_WIDTH, FULL_MENU_WIDTH],
    })

    return (
      <>
        <View style={styles.inlineExpandableRoot}>
          <Animated.View style={[styles.inlineExpandableMenu, { width: inlineWidth }]}>
            {menu}
          </Animated.View>
        </View>
        {isOpen ? (
          <Pressable
            onPress={onClose}
            accessibilityLabel={t('nav.closeMenu')}
            accessibilityRole="button"
            style={styles.inlineDismissLayer}
          />
        ) : null}
      </>
    )
  }

  if (!shouldRenderOverlay) {
    return null
  }

  const translateX = slideProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [expandsFromCompact ? 0 : -menuWidth, 0],
  })
  const overlayWidth = expandsFromCompact
    ? slideProgress.interpolate({
        inputRange: [0, 1],
        outputRange: [collapsedWidth, menuWidth],
      })
    : menuWidth
  const scrimOpacity = slideProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 1],
  })

  // On Android Chrome, a position:fixed element's top/bottom can resolve
  // against the "layout" viewport (the size while the address bar is
  // visible) rather than the current "visual" viewport, leaving a gap the
  // size of the address bar if it was already collapsed before the menu
  // opened. visualViewport.height reports the real visible height. Reading
  // it directly here (no state/effect) is enough — the background can't
  // scroll while the menu is open, so it can't go stale mid-open.
  const visualViewportHeight = typeof window !== 'undefined' ? window.visualViewport?.height : undefined

  return (
    <Animated.View
      pointerEvents={isOpen ? 'auto' : 'none'}
      style={[styles.overlayRoot, visualViewportHeight !== undefined ? { height: visualViewportHeight } : null]}
    >
      <Animated.View style={[styles.scrim, { opacity: scrimOpacity }]}>
        <Pressable
          onPress={onClose}
          accessibilityLabel={t('nav.closeMenu')}
          accessibilityRole="button"
          style={StyleSheet.absoluteFill}
        />
      </Animated.View>
      <Animated.View
        style={[
          styles.overlayMenu,
          {
            width: overlayWidth,
            transform: [{ translateX }],
          },
        ]}
      >
        {menu}
      </Animated.View>
    </Animated.View>
  )
}

const styles = StyleSheet.create({
  menu: {
    height: '100%',
    position: 'relative',
  },
  version: {
    position: 'absolute',
    bottom: 8,
    left: 0,
    right: 16,
    fontSize: 10,
    textAlign: 'right',
    color: 'rgba(255,255,255,0.2)',
  },
  inlineExpandableRoot: {
    height: '100%',
    width: COMPACT_MENU_WIDTH,
    zIndex: 10,
  },
  inlineExpandableMenu: {
    bottom: 0,
    height: '100%',
    left: 0,
    overflow: 'hidden',
    position: 'absolute',
    top: 0,
  },
  inlineDismissLayer: {
    bottom: 0,
    left: COMPACT_MENU_WIDTH,
    position: 'absolute',
    right: 0,
    top: 0,
    zIndex: 5,
  },
  overlayRoot: {
    ...StyleSheet.absoluteFillObject,
    // 'absolute' pins this to the layout root's box rather than the
    // viewport; 'fixed' plus the explicit visualViewportHeight above is
    // what actually keeps this correct regardless of the page's scroll
    // height or address-bar state. This is a web-only component (the
    // mobile admin dashboard doesn't render it on native) — RN's shared
    // style types don't include the CSS 'fixed' value react-native-web
    // supports, hence the cast.
    position: 'fixed' as 'absolute',
    zIndex: 20,
  },
  scrim: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: colors.scrim,
  },
  overlayMenu: {
    bottom: 0,
    left: 0,
    overflow: 'hidden',
    position: 'absolute',
    top: 0,
    zIndex: 1,
  },
})
